/**
 * The gate.
 *
 * One field that matters and one that is worth filling in. The key is held in
 * memory for the life of the tab (see src/api.ts); the moderator handle is what
 * lands in the audit row next to every case read (§07), so the copy asks for it
 * plainly rather than hiding it in a settings screen nobody opens.
 */
import { useState } from 'react';
import type { FormEvent } from 'react';
import { apiBase, ApiError } from '../api';

export default function Gate({
  onSubmit,
  busy,
  error,
}: {
  onSubmit: (key: string, actor: string) => void;
  busy: boolean;
  error: ApiError | null;
}) {
  const [key, setKey] = useState('');
  const [actor, setActor] = useState('');

  function submit(e: FormEvent) {
    e.preventDefault();
    if (key.trim().length === 0 || busy) return;
    onSubmit(key.trim(), actor);
  }

  const unconfigured = error?.code === 'admin_unconfigured' || error?.code === 'admin_key_too_weak';

  return (
    <div className="gate">
      <form className="gate-card" onSubmit={submit} autoComplete="off">
        <div>
          <div className="eyebrow">Alap · trust and safety</div>
          <h1>Moderation console</h1>
        </div>

        <p className="lede">
          The queue, the guardrails and the waitlist for the West Bengal launch. Sign in with the
          console key issued to you.
        </p>

        <div className="field">
          <label htmlFor="admin-key">Admin key</label>
          <input
            id="admin-key"
            className="input"
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            placeholder="x-alap-admin-key"
            disabled={busy}
            /* eslint-disable-next-line jsx-a11y/no-autofocus */
            autoFocus
          />
        </div>

        <div className="field">
          <label htmlFor="admin-actor">Your handle</label>
          <input
            id="admin-actor"
            className="input"
            type="text"
            value={actor}
            onChange={(e) => setActor(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            placeholder="e.g. rupsa.m"
            disabled={busy}
          />
          <span className="hint">
            Goes into the audit log beside everything you open. Leave it blank and the log records
            the key's fingerprint instead — which names nobody.
          </span>
        </div>

        {error && (
          <div className="notice is-bad" role="alert">
            <strong>{unconfigured ? 'The console is switched off on this server.' : 'Not signed in.'}</strong>
            <br />
            {error.message}
            {error.status === 401 && (
              <>
                <br />
                Check you pasted the whole key — it is compared in full, and a trailing space counts.
              </>
            )}
          </div>
        )}

        <button className="btn btn-primary" type="submit" disabled={busy || key.trim().length === 0}>
          {busy ? 'Checking…' : 'Unlock console'}
        </button>

        <div className="gate-note">
          <div>
            <strong>The key is held in memory only.</strong> It is never written to localStorage,
            sessionStorage or a cookie, so closing this tab signs you out and a shared workstation
            keeps nothing. Every profile, report and message you open writes an audit row against
            your handle (§07).
          </div>
        </div>

        <div className="eyebrow" style={{ textAlign: 'center' }}>
          API · {apiBase}
        </div>
      </form>
    </div>
  );
}
